import { Fragment, useState, type FormEvent } from 'react';
import { get, post } from '../api';
import { useAuth } from '../auth';
import { ComposeButtons, inviteLinks, type InviteCompose } from '../components/ComposeButtons';
import { CopyLink } from '../components/CopyLink';
import { Field, Problems } from '../components/Field';
import { toast } from '../components/toast';
import { describe } from '../errors';
import { useLoad } from '../hooks';
import { AccessPill, RowActions, day, useAccessActions, type Access } from './userAccess';

interface StaffUser {
  id: number;
  email: string;
  display_name: string;
  role: 'owner' | 'admin';
  is_active: boolean;
  access: Access;
  has_password: boolean;
  invited_at: string | null;
  last_login_at: string | null;
}

interface Sent {
  emailed?: boolean;
  link?: string;
  compose?: InviteCompose;
}

/** The Office users tab of the Users page: who can sign in to the office. Owner-only: granting office access is in the same league as billing. */
export function StaffUsers() {
  const [tick, setTick] = useState(0);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  /** Set when nobody was emailed (email is off or failed), so the office can pass the link on. */
  const [link, setLink] = useState('');
  /** The invite ready to send from this device, when it is set to go by hand or could not go. */
  const [compose, setCompose] = useState<InviteCompose | undefined>();
  const { user } = useAuth();
  const list = useLoad(() => get<{ items: StaffUser[] }>('/api/staff-users'), [tick]);
  const actions = useAccessActions(
    '/api/staff-users',
    () => setTick((t) => t + 1),
    () => {
      setLink('');
      setCompose(undefined);
      setProblems([]);
      setFields({});
    },
  );

  async function invite(e: FormEvent) {
    e.preventDefault();
    setProblems([]);
    setFields({});
    setLink('');
    setCompose(undefined);
    actions.setLink(null);
    actions.setProblems([]);
    try {
      const r = await post<Sent>('/api/staff-users', { displayName, email });
      toast(r.emailed ? `Invitation sent to ${email}.` : `Account created for ${email}.`);
      setLink(r.link ?? '');
      setCompose(r.compose);
      setEmail('');
      setDisplayName('');
      setTick((t) => t + 1);
    } catch (err) {
      const d = describe(err);
      setProblems(d.problems);
      setFields(d.fields);
    }
  }

  const items = list.data?.items ?? [];
  return (
    <>
      <p className="muted">
        Who can sign in to the office. Everyone here can manage the CRM; only the owner can also
        change settings, tax codes and third-party credentials.
      </p>
      <form onSubmit={invite} className="stack">
        <fieldset>
          <legend>Invite someone</legend>
          <Field label="Their name" error={fields.displayName}>
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
          </Field>
          <Field label="Their email" error={fields.email} hint="We will send the invitation here.">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>
          <button type="submit" disabled={displayName.trim() === '' || email.trim() === ''}>
            Send invitation
          </button>
        </fieldset>
      </form>
      {link && <CopyLink key={link} link={link} />}
      <ComposeButtons items={inviteLinks(compose)} />
      <Problems problems={[...problems, ...actions.problems]} title="Could not do that." focus />
      {list.error && (
        <p role="alert" className="error">
          {list.error}
        </p>
      )}
      {list.loading && !list.data && <p className="muted">Loading…</p>}
      {!list.loading && !list.error && items.every((u) => u.id === user?.id) && (
        <p className="empty">No one else has office access yet. Invite someone above.</p>
      )}
      {items.length > 0 && (
        <>
          <h2>People with office access</h2>
          <table className="people">
            <caption className="sr-only">People with office access</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Role</th>
                <th scope="col">Status</th>
                <th scope="col">Last sign-in</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => {
                const isSelf = u.id === user?.id;
                const person = { ...u, name: u.display_name || u.email };
                return (
                  <Fragment key={u.id}>
                    <tr>
                      <td data-label="Name">
                        {u.display_name || '—'}
                        {isSelf && <span className="muted"> You</span>}
                      </td>
                      <td data-label="Email">{u.email}</td>
                      <td data-label="Role">{u.role === 'owner' ? 'Owner' : 'Admin'}</td>
                      <td data-label="Status">
                        <AccessPill access={u.access} invitedAt={u.invited_at} />
                      </td>
                      <td data-label="Last sign-in">
                        {u.access === 'invited' || u.access === 'expired'
                          ? '—'
                          : day(u.last_login_at)}
                      </td>
                      <td className="actions">
                        <RowActions
                          person={person}
                          actions={actions}
                          isSelf={isSelf}
                          canSwitchOff={u.role !== 'owner'}
                        />
                      </td>
                    </tr>
                    {actions.link?.id === u.id && (
                      <tr className="link-row">
                        <td colSpan={6}>
                          {actions.link.url && (
                            <CopyLink
                              key={actions.link.url}
                              link={actions.link.url}
                              purpose={actions.link.purpose}
                            />
                          )}
                          <ComposeButtons items={inviteLinks(actions.link.compose)} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}
